import { z } from 'zod';
import { issue } from '@/lib/forms/messages';
import type { CadCapability } from '@services/settings.service';

export type CadFileLimits = Pick<CadCapability, 'allowedExtensions' | 'maxFileSizeMb'>;

// Mirrors Path(filename).suffix.lower(): a lone leading dot is no extension ('.stp' -> '').
export const fileExtensionOf = (filename: string) => {
  const cut = filename.lastIndexOf('.');
  return cut > 0 ? filename.slice(cut).toLowerCase() : '';
};

/** Validates against the reported limits; these used to be constants kept in step by hand. */
export const makeCadFileSchema = ({ allowedExtensions, maxFileSizeMb }: CadFileLimits) => {
  const maxBytes = maxFileSizeMb * 1024 * 1024;
  return z
    .object({ name: z.string(), size: z.number() })
    .superRefine((file, ctx) => {
      const extension = fileExtensionOf(file.name);
      if (!allowedExtensions.includes(extension)) {
        const formats = allowedExtensions.join(', ');
        // `.stp` has no extension by Path.suffix rules; quoting it as one prints it twice.
        ctx.addIssue(extension
          ? issue('cad.upload.unsupportedFormat', ['name'], { extension, formats })
          : issue('cad.upload.noExtension', ['name'], { name: file.name, formats }));
      }
      if (file.size > maxBytes) {
        ctx.addIssue(issue('cad.upload.fileTooLarge', ['size'], { size: maxFileSizeMb }));
      }
    });
};

export const isAcceptedCADFile = (file: File, limits: CadFileLimits) =>
  makeCadFileSchema(limits).safeParse(file).success;
